import * as React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import withTheme from '../utils/withTheme';
import { DefaultProps, handleDefaultStyle } from '../utils';
import { handleSize, handleVariant } from './style';

const renderComponent = (component = 'button') => {
	if (!styled[component]) {
		component = 'button';
	}
	return styled[component]`
		display: inline-block;
		font-weight: 400;
		color: ${({ theme: { defaultColor } }) => defaultColor};
		text-align: center;
		vertical-align: middle;
		user-select: none;
		background-color: transparent;
		border: 1px solid transparent;
		transition: color 0.15s ease-in-out,
		background-color 0.15s ease-in-out,
		border-color 0.15s ease-in-out,
		box-shadow 0.15s ease-in-out;
		${({ fullWidth = false }) => (fullWidth ? 'width: 100%;' : '')}
		${({ disabled }) => {
			if (disabled) {
				return 'cursor: not-allowed;';
			}
			if (component === 'button') {
				return 'cursor: pointer;';
			}
			return '';
		}}
		${handleSize}
		${handleVariant}
		${handleDefaultStyle}
	`;
};

const Button = withTheme(
	React.forwardRef(function Button(
		{
			theme,
			type = 'button',
			children,
			className,
			component = 'button',
			disabled,
			href,
			value,
			toolTipPosition = 'top', // eslint-disable-line
			...rest
		},
		ref
	) {
		let Component =
			typeof component === 'string' && component ? component : 'button';
		const ComponentProp = {
			...rest,
			className,
			href,
			value,
			theme,
			disabled,
		};
		if (Component === 'button' && typeof href === 'string') {
			Component = 'a';
		}
		if (Component === 'button') {
			ComponentProp.type = type;
		}
		if (Component === 'input' && typeof value === 'undefined') {
			console.error(
				new Error(
					'input is a void element tag and must neither have `children` nor use `dangerouslySetInnerHTML`'
				)
			);
		}
		Component = renderComponent(Component);
		if (children) {
			return (
				<Component ref={ref} data-testid='ButtonComponent' {...ComponentProp}>
					{children}
				</Component>
			);
		}
		return (
			<Component ref={ref} data-testid='ButtonComponent' {...ComponentProp} />
		);
	})
);

Button.propTypes = {
	/**
	 * The Content of the button
	 */
	children: PropTypes.node,
	/**
	 * Text displayed inside button
	 */
	value: PropTypes.string,
	/**
	 * Style class of the button
	 */
	className: PropTypes.string,
	/**
	 * ID of the button
	 */
	id: PropTypes.string,
	/**
	 * type of button
	 */
	type: PropTypes.string,
	/**
	 * The Component used for the root node
	 * Either a string to use a DOM element or a component
	 */
	component: PropTypes.elementType,
	/**
	 * If `true`, the button will be disabled.
	 */
	disabled: PropTypes.bool,
	/**
	 * Size of the button.
	 */
	size: PropTypes.oneOf(['lg', 'md', 'lg']),
	/**
	 * variant type of style of button
	 * you can define custom variant using themeprovider
	 */
	variant: PropTypes.string,
	/**
	 * get outline version of variant
	 */
	outline: PropTypes.bool,
	/**
	 * The URL to link to when the button is clicked.
	 * If defined, an `a` element will be used as the root node.
	 */
	href: PropTypes.string,
	/**
	 * If `true`, the button will take up the full width of its container.
	 */
	fullWidth: PropTypes.bool,
	...DefaultProps,
};

Button.defaultProps = {
	component: 'button',
	type: 'button',
	disabled: false,
	size: 'md',
	variant: 'default',
};

Button.displayName = 'NbButton';

export default Button;
